/**
 * MobileTrainingMatrixStaff — Training Matrix mobile view ( <lg ).
 * Batch 6.2 — Option A (Staff Picker → individual staff drill).
 *
 * Consumes the EXACT SAME `/training/matrix` payload as the desktop
 * cross-view (rows × columns) — no new endpoint, no client-side status
 * derivation. Cell.status is authoritative from the server.
 *
 * The rotated-header wide grid stays on desktop. On mobile we present:
 *   Scope selector (Mandatory / Required / All)
 *   Staff picker (native <select>) with role in the label
 *   Selected staff → header (name + role)
 *     → Vertical list of that staff's courses:
 *          Course name (+ requirement type)
 *          Status pill (colour + icon + text)
 *          Expiry date if present
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Check, Clock, X as XIcon, Minus,
  User as UserIcon, ChevronDown,
} from "lucide-react";
import { MobileFilterDrawer } from "@/components/mobile";

const CELL_META = {
  compliant:     { label: "Compliant",     Icon: Check,    cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  expiring:      { label: "Expiring",      Icon: Clock,    cls: "bg-amber-100 text-amber-900 border-amber-300" },
  expired:       { label: "Expired",       Icon: XIcon,    cls: "bg-red-100 text-red-800 border-red-300" },
  missing:       { label: "Missing",       Icon: XIcon,    cls: "bg-red-50 text-red-800 border-red-200" },
  in_progress:   { label: "In Progress",   Icon: Clock,    cls: "bg-sky-100 text-sky-800 border-sky-300" },
  not_completed: { label: "Not Completed", Icon: Minus,    cls: "bg-slate-100 text-slate-700 border-slate-300" },
  not_required:  { label: "N/A",           Icon: Minus,    cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const ROLE_OPTIONS = [
  { v: "care_worker",         label: "Care Worker" },
  { v: "manager",             label: "Manager" },
  { v: "deputy_manager",      label: "Deputy Manager" },
  { v: "service_manager",     label: "Service Manager" },
  { v: "registered_manager",  label: "Registered Manager" },
  { v: "admin",               label: "Admin" },
];

function StatusPill({ status, testid }) {
  const s = CELL_META[status] || CELL_META.not_required;
  const Icon = s.Icon;
  return (
    <span
      data-testid={testid}
      data-status={status}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.cls}`}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function MobileTrainingMatrixStaff({ onOpenStaff, refreshTick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("mandatory");
  const [siteId, setSiteId] = useState("");
  const [role, setRole] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [sites, setSites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { course_scope: scope };
      if (siteId) params.site_id = siteId;
      if (role) params.role = role;
      const r = await api.get("/training/matrix", { params });
      setData(r.data);
      // If the previously-picked staff has disappeared from the filtered
      // rows, reset selection so we always show a valid row.
      if (selectedStaffId && !(r.data?.rows || []).some((x) => x.user_id === selectedStaffId)) {
        setSelectedStaffId("");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, siteId, role]);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => { api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {}); }, []);

  const rows = data?.rows || [];
  const columns = data?.columns || [];
  const selected = rows.find((r) => r.user_id === selectedStaffId);

  const activeFilterCount = (siteId ? 1 : 0) + (role ? 1 : 0);

  return (
    <div className="space-y-3" data-testid="mobile-training-matrix">
      {/* Scope + Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex-1 min-w-[160px]">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Scope</span>
          <select
            value={scope} onChange={(e) => setScope(e.target.value)}
            data-testid="mobile-matrix-scope"
            className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2"
          >
            <option value="required">Required (mandatory + role)</option>
            <option value="mandatory">Mandatory only</option>
            <option value="all">All active courses</option>
          </select>
        </label>
        <div className="self-end">
          <MobileFilterDrawer
            triggerLabel="Filters"
            activeCount={activeFilterCount}
            onClear={() => { setSiteId(""); setRole(""); }}
            testid="mobile-matrix-filter-drawer"
          >
            <div className="space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Site</span>
                <select
                  value={siteId} onChange={(e) => setSiteId(e.target.value)}
                  data-testid="mobile-filter-matrix-site"
                  className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2"
                >
                  <option value="">All sites</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Role</span>
                <select
                  value={role} onChange={(e) => setRole(e.target.value)}
                  data-testid="mobile-filter-matrix-role"
                  className="mt-0.5 w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2 capitalize"
                >
                  <option value="">All roles</option>
                  {ROLE_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                </select>
              </label>
            </div>
          </MobileFilterDrawer>
        </div>
      </div>

      {/* Staff picker */}
      <label className="block" data-testid="mobile-matrix-picker-wrap">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Select staff member</span>
        <div className="relative mt-0.5">
          <select
            value={selectedStaffId}
            onChange={(e) => setSelectedStaffId(e.target.value)}
            data-testid="mobile-matrix-staff-picker"
            className="w-full min-h-[44px] h-11 text-sm border border-slate-300 rounded-md bg-white px-2 pr-8 appearance-none"
          >
            <option value="">— Pick a staff member —</option>
            {rows.map((r) => (
              <option key={r.user_id} value={r.user_id}>
                {r.user_name} · {String(r.user_role || "").replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </label>

      {loading ? (
        <div className="text-sm text-slate-500 p-4" data-testid="mobile-matrix-loading">Loading matrix…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-matrix-empty">
          <div className="text-sm font-semibold text-slate-700">No staff members match the current filters.</div>
        </div>
      ) : !selected ? (
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center" data-testid="mobile-matrix-pick-prompt">
          <UserIcon className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-600">
            Pick a staff member above to view their course list.
          </div>
          <div className="text-[11px] text-slate-500 mt-1">
            {rows.length} staff member{rows.length === 1 ? "" : "s"} in scope
          </div>
        </div>
      ) : (
        <>
          {/* Selected staff header */}
          <div
            className="rounded-lg border border-slate-200 bg-white p-3 flex items-center gap-3"
            data-testid={`mobile-matrix-selected-header-${selected.user_id}`}
          >
            <div className="w-10 h-10 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-sky-700" />
            </div>
            <div className="min-w-0 flex-1">
              <button
                onClick={() => onOpenStaff?.(selected.user_id)}
                data-testid={`mobile-matrix-open-staff-${selected.user_id}`}
                className="font-heading text-base font-semibold text-slate-900 hover:text-sky-700 hover:underline text-left truncate w-full"
              >
                {selected.user_name}
              </button>
              <div className="text-[11px] text-slate-500 capitalize">
                {String(selected.user_role || "").replace(/_/g, " ")}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Courses</div>
              <div className="font-heading text-sm font-semibold tabular-nums text-slate-900">
                {selected.cells.length}
              </div>
            </div>
          </div>

          {/* Course list — vertical, one row per column */}
          <ul className="space-y-2" data-testid="mobile-matrix-course-list">
            {selected.cells.map((cell, ix) => {
              const col = columns[ix] || {};
              const meta = CELL_META[cell.status] || CELL_META.not_required;
              return (
                <li
                  key={col.id || ix}
                  data-testid={`mobile-matrix-course-${selected.user_id}-${col.id || ix}`}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 truncate" title={col.name}>
                        {col.name || "—"}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 capitalize">
                        {col.category || "—"}{col.requirement_type ? ` · ${String(col.requirement_type).replace(/_/g, " ")}` : ""}
                      </div>
                    </div>
                    <StatusPill status={cell.status} testid={`mobile-matrix-status-${col.id || ix}`} />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-600 flex items-center gap-1">
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Expiry</span>
                    <span
                      className={`tabular-nums ${
                        cell.status === "expired" ? "text-red-700 font-semibold"
                          : cell.status === "expiring" ? "text-amber-800 font-semibold"
                          : "text-slate-700"
                      }`}
                      data-testid={`mobile-matrix-expiry-${col.id || ix}`}
                    >
                      {cell.expiry_date ? cell.expiry_date : (cell.status === "not_required" ? "N/A" : "—")}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
